<template>
  <div class="w-full max-w-md">
    <div class="text-center mb-8">
      <h1 class="font-bold text-2xl text-foreground">{{ tt('豚鼠', '豚鼠') }}</h1>
      <p class="mt-2 text-muted-foreground">{{ tt('欢迎回到阅读社区', '歡迎回到閱讀社區') }}</p>
    </div>

    <div class="rounded-[24px] border border-border/80 bg-card p-6 shadow-card">
      <h2 class="text-2xl font-bold text-center text-foreground">{{ tt('登录', '登入') }}</h2>
      <p class="mt-1 text-center text-sm text-muted-foreground">{{ tt('输入邮箱和密码登录账户', '輸入郵箱和密碼登入帳戶') }}</p>

      <div class="mt-6" data-testid="login-form">
        <div
          v-if="errorMessage"
          class="mb-4 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
          role="alert"
        >
          {{ errorMessage }}
          <button
            v-if="showResend"
            type="button"
            class="mt-2 block font-medium underline disabled:opacity-50"
            :disabled="resendLoading"
            @click="resendVerification"
          >
            {{ resendLoading ? tt('发送中...', '發送中...') : tt('重新发送验证邮件', '重新發送驗證郵件') }}
          </button>
          <p v-if="resendMessage" class="mt-1">{{ resendMessage }}</p>
        </div>

        <form class="space-y-4" @submit.prevent="handleLogin">
          <div class="space-y-2">
            <label for="email" class="text-sm font-medium text-foreground">{{ tt('邮箱', '郵箱') }}</label>
            <input
              id="email"
              v-model="email"
              type="email"
              autocomplete="email"
              required
              :placeholder="tt('请输入邮箱', '請輸入郵箱')"
              :class="
                fieldErrors.email
                  ? 'h-11 w-full rounded-xl border border-red-300 bg-background px-3 text-sm focus:border-red-500 focus:outline-none'
                  : 'h-11 w-full rounded-xl border border-border bg-background px-3 text-sm'
              "
              @input="clearFieldError('email')"
            />
            <p v-if="fieldErrors.email" class="text-sm text-red-600">{{ fieldErrors.email }}</p>
          </div>

          <div class="space-y-2">
            <label for="password" class="text-sm font-medium text-foreground">{{ tt('密码', '密碼') }}</label>
            <div class="relative">
              <input
                id="password"
                v-model="password"
                :type="showPassword ? 'text' : 'password'"
                autocomplete="current-password"
                required
                :placeholder="tt('请输入密码', '請輸入密碼')"
                :class="
                  fieldErrors.password
                    ? 'h-11 w-full rounded-xl border border-red-300 bg-background px-3 pr-10 text-sm focus:border-red-500 focus:outline-none'
                    : 'h-11 w-full rounded-xl border border-border bg-background px-3 pr-10 text-sm'
                "
                @input="clearFieldError('password')"
              />
              <button
                type="button"
                class="absolute top-1/2 right-3 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                :aria-label="showPassword ? tt('隐藏密码', '隱藏密碼') : tt('显示密码', '顯示密碼')"
                @click="showPassword = !showPassword"
              >
                <svg
                  v-if="showPassword"
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
                  <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" x2="23" y1="1" y2="23" />
                </svg>
                <svg
                  v-else
                  class="h-4 w-4"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>
            <p v-if="fieldErrors.password" class="text-sm text-red-600">{{ fieldErrors.password }}</p>
          </div>

          <button
            type="submit"
            class="inline-flex h-11 w-full items-center justify-center rounded-xl bg-brand text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
            :disabled="isLoading"
          >
            {{ isLoading ? tt('登录中...', '登入中...') : tt('登录', '登入') }}
          </button>
        </form>

        <div class="mt-6">
          <div class="flex items-center gap-4">
            <div class="h-px flex-1 bg-border" />
            <span class="text-sm text-muted-foreground">{{ tt('或', '或') }}</span>
            <div class="h-px flex-1 bg-border" />
          </div>
          <div ref="googleBtnRef" class="mt-4 flex justify-center" />
        </div>

        <div class="mt-6 space-y-2 text-center">
          <p class="text-sm text-muted-foreground">
            {{ tt('还没有账户？', '還沒有帳戶？') }}
            <a :href="registerHref" class="font-medium text-brand hover:underline">{{ tt('去注册', '去註冊') }}</a>
          </p>
          <a :href="getLocalePrefix() + '/auth/forgot-password'" class="text-sm text-muted-foreground hover:text-foreground">
            {{ tt('忘记密码？', '忘記密碼？') }}
          </a>
        </div>
      </div>
    </div>

    <div class="text-center mt-8">
      <a :href="getLocalePrefix() || '/'" class="text-sm text-muted-foreground hover:text-foreground">{{ tt('返回首页', '返回首頁') }}</a>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { GOOGLE_GIS_CLIENT_ID, authSwitchHref, rememberTunshuUser, safeInternalPath } from './login.ts'
import { tt, getLocalePrefix, i18nFetch } from '../../shared/i18n.ts'

const props = withDefaults(
  defineProps<{
    redirect?: string
    googleClientId?: string
  }>(),
  { redirect: '/', googleClientId: GOOGLE_GIS_CLIENT_ID },
)

const email = ref('')
const password = ref('')
const showPassword = ref(false)
const isLoading = ref(false)
const errorMessage = ref('')
const showResend = ref(false)
const resendLoading = ref(false)
const resendMessage = ref('')
const fieldErrors = ref({ email: '', password: '' })

interface LoginBody {
  code?: number
  reason?: string
  message?: string
  data?: {
    redirect?: string
    user?: { id?: string; name?: string; avatarUrl?: string }
  }
}

const registerHref = computed(() => authSwitchHref('/auth/register'))

const REASON_EMAIL_NOT_VERIFIED = 'error.auth.email_not_verified'
const REASON_USER_SUSPENDED = 'error.user.suspended'
const REASON_ALREADY_DELETED = 'error.object.already_deleted'
const googleBtnRef = ref<HTMLElement | null>(null)

function clearFieldError(field: 'email' | 'password') {
  fieldErrors.value[field] = ''
  if (errorMessage.value) {
    errorMessage.value = ''
  }
}

function mapLoginError(status: number, body: LoginBody): void {
  const message = body.message || ''
  const reason = body.reason || ''
  showResend.value = false
  resendMessage.value = ''
  if (reason === REASON_EMAIL_NOT_VERIFIED) {
    errorMessage.value = tt('邮箱还没验证，请先打开验证邮件', '郵箱還沒驗證，請先打開驗證郵件')
    showResend.value = true
    return
  }
  if (reason === REASON_USER_SUSPENDED || reason === REASON_ALREADY_DELETED) {
    errorMessage.value = tt('账号不可用', '帳號不可用')
    return
  }
  if (status === 400 || message.includes('400')) {
    errorMessage.value = tt('请求错误。请检查输入后重试。', '請求錯誤。請檢查輸入後重試。')
    return
  }
  if (status === 401 || message.includes('401') || /unauthorized/i.test(message)) {
    errorMessage.value = tt('邮箱或密码无效。请检查后重试。', '郵箱或密碼無效。請檢查後重試。')
    return
  }
  if (status === 404 || message.includes('404')) {
    errorMessage.value = tt('未找到账户。请检查邮箱地址。', '未找到帳戶。請檢查郵箱地址。')
    fieldErrors.value.email = tt('未找到账户', '未找到帳戶')
    return
  }
  if (status === 429 || message.includes('429')) {
    errorMessage.value = tt('登录尝试次数过多。请稍后再试。', '登入嘗試次數過多。請稍後再試。')
    return
  }
  if (/network/i.test(message) || message.includes('网络')) {
    errorMessage.value = tt('网络错误。请检查连接后重试。', '網絡錯誤。請檢查連接後重試。')
    return
  }
  if (/timeout/i.test(message) || message.includes('超时')) {
    errorMessage.value = tt('请求超时。请重试。', '請求超時。請重試。')
    return
  }
  errorMessage.value = message || tt('登录失败。请重试。', '登入失敗。請重試。')
}

async function handleLogin() {
  errorMessage.value = ''
  fieldErrors.value.email = ''
  fieldErrors.value.password = ''
  isLoading.value = true

  try {
    const queryRedirect = new URLSearchParams(window.location.search).get('redirect')
    const redirect = safeInternalPath(queryRedirect || props.redirect)
    const res = await i18nFetch('/bff/v1/webauth/login', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({
        email: email.value,
        password: password.value,
        redirect,
      }),
    })

    const body = (await res.json().catch(() => ({}))) as LoginBody
    if (!res.ok || (typeof body.code === 'number' && body.code !== 0)) {
      console.error('[Login] webauth login failed', res.status, body.code, body.reason, body.message)
      mapLoginError(res.status, body)
      return
    }

    rememberTunshuUser(body.data?.user)
    const next = safeInternalPath(body.data?.redirect || redirect)
    window.location.assign(next)
  } catch (error: unknown) {
    console.error('[Login] handleLogin error:', error)
    const message = error instanceof Error ? error.message : String(error)
    mapLoginError(0, { message })
  } finally {
    isLoading.value = false
  }
}

async function resendVerification() {
  resendLoading.value = true
  resendMessage.value = ''
  try {
    const res = await i18nFetch('/bff/v1/webauth/email/resend', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body: JSON.stringify({ email: email.value }),
    })
    if (res.status === 429) {
      resendMessage.value = tt('60 秒后再试', '60 秒後再試')
      return
    }
    if (!res.ok) {
      resendMessage.value = tt('这会儿发不出去，稍后再试', '這會兒發不出去，稍後再試')
      return
    }
    resendMessage.value = tt('验证邮件已发出，请查收。没看到就翻翻垃圾箱。', '驗證郵件已發出，請查收。沒看到就翻翻垃圾箱。')
  } catch (error: unknown) {
    console.error('[Login] resend verification failed:', error)
    resendMessage.value = tt('这会儿发不出去，稍后再试', '這會兒發不出去，稍後再試')
  } finally {
    resendLoading.value = false
  }
}

async function postGoogleIDToken(idToken: string) {
  const queryRedirect = new URLSearchParams(window.location.search).get('redirect')
  const redirect = safeInternalPath(queryRedirect || props.redirect)
  const res = await i18nFetch('/bff/v1/webauth/google', {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify({ idToken, redirect }),
  })
  const body = (await res.json().catch(() => ({}))) as LoginBody
  if (!res.ok || (typeof body.code === 'number' && body.code !== 0)) {
    console.error('[Login] webauth google failed', res.status, body.code, body.reason, body.message)
    mapLoginError(res.status, body)
    return
  }
  rememberTunshuUser(body.data?.user)
  window.location.assign(safeInternalPath(body.data?.redirect || redirect))
}

function loadGisScript(): Promise<void> {
  if (window.google?.accounts?.id) {
    return Promise.resolve()
  }
  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]')
    if (existing) {
      existing.addEventListener('load', () => resolve())
      existing.addEventListener('error', () => reject(new Error('load GIS failed')))
      return
    }
    const script = document.createElement('script')
    script.src = 'https://accounts.google.com/gsi/client'
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('load GIS failed'))
    document.head.appendChild(script)
  })
}

onMounted(async () => {
  const clientId = (props.googleClientId || GOOGLE_GIS_CLIENT_ID).trim()
  if (!clientId || !googleBtnRef.value) {
    return
  }
  try {
    await loadGisScript()
    window.google?.accounts.id.initialize({
      client_id: clientId,
      ux_mode: 'popup',
      callback: (resp) => {
        const token = resp?.credential?.trim()
        if (!token) {
          console.error('[Login] GIS callback missing credential')
          errorMessage.value = tt('Google 登录失败，请重试。', 'Google 登入失敗，請重試。')
          return
        }
        void postGoogleIDToken(token)
      },
    })
    window.google?.accounts.id.renderButton(googleBtnRef.value, {
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      width: 320,
    })
  } catch (error) {
    console.error('[Login] init GIS failed', error)
  }
})
</script>
